import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { useGetNotificationsQuery, useDeleteNotificationMutation, useClearAllNotificationsMutation, resolveNotificationPath, NotificationRecord } from '../features/notifications/notificationApi';

const NotificationBell = () => {
    const { t } = useTranslation();

    function timeAgo(dateStr: string): string {
        const diffMs = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return t('notifications.justNow');
        if (mins < 60) return t('notifications.minutesAgo', { count: mins });
        const hours = Math.floor(mins / 60);
        if (hours < 24) return t('notifications.hoursAgo', { count: hours });
        const days = Math.floor(hours / 24);
        return t('notifications.daysAgo', { count: days });
    }

    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    // No push/socket channel for these yet, so poll while the bell is
    // mounted (i.e. for the whole admin session) to catch new uploads
    // reasonably fast without the user needing to refresh the page.
    const { data: notifications } = useGetNotificationsQuery(undefined, { pollingInterval: 60000 });
    const [deleteNotification] = useDeleteNotificationMutation();
    const [clearAllNotifications] = useClearAllNotificationsMutation();

    useOutsideClick(ref, () => {
        if (isOpen) setIsOpen(false);
    });

    const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

    // Clicking a notification clears it (removes it from the list) rather
    // than just marking it read — it's a one-shot alert, not something to
    // keep around once acknowledged. Still navigates first, so the click
    // target the notification pointed to isn't lost.
    const handleNotificationClick = (n: NotificationRecord) => {
        setIsOpen(false);
        const path = resolveNotificationPath(n);
        if (path) navigate(path);
        deleteNotification(n.id);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-50 rounded-full transition-colors focus:outline-none"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 rtl:right-auto rtl:left-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-gray-900">{t('notifications.title')}</p>
                            {unreadCount > 0 && (
                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-wide">{t('notifications.newCount', { count: unreadCount })}</span>
                            )}
                        </div>
                        {notifications && notifications.length > 0 && (
                            <button
                                onClick={() => clearAllNotifications()}
                                className="text-[10px] font-black text-gray-400 hover:text-red-600 uppercase tracking-wide transition-colors shrink-0"
                            >
                                {t('notifications.clearAll')}
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
                        {!notifications || notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center text-sm text-gray-400">{t('notifications.noNotificationsYet')}</div>
                        ) : (
                            notifications.slice(0, 20).map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => handleNotificationClick(n)}
                                    className={`w-full text-left rtl:text-right px-4 py-3 hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                                >
                                    <div className="flex items-start gap-2">
                                        {!n.is_read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />}
                                        <div className={n.is_read ? 'pl-3.5 rtl:pl-0 rtl:pr-3.5' : ''}>
                                            <p className="text-xs font-bold text-gray-900">{n.title}</p>
                                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{n.message}</p>
                                            <p className="text-[10px] text-gray-400 mt-1 font-medium">{timeAgo(n.created_at)}</p>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
